// Tabs (CONTRACT "Coin Checker" tabs): 32 px, selected is tonal fill plus 600,
// count muted. Arrow keys move between tabs (roving tabindex). With hashParam
// set (for example "tab"), the selected id is mirrored into the URL hash as
// "#tab=<id>". The hash is written with replaceState (which fires no event),
// and hash-to-state applies only on real hashchange and popstate events, with
// a ref guarding against the component's own writes, so state and hash can
// never ping-pong (the redo-1 P0: two selections used to loop forever).
import { useEffect, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { cx } from './cx';
import './Tabs.css';

export interface TabItem<T extends string> {
  readonly id: T;
  readonly label: string;
  readonly count?: number | string;
  readonly disabled?: boolean;
}

export interface TabsProps<T extends string> {
  readonly tabs: ReadonlyArray<TabItem<T>>;
  readonly selected: T;
  readonly onSelect: (id: T) => void;
  readonly label: string;
  readonly hashParam?: string;
  readonly className?: string;
  readonly end?: ReactNode;
  // True only where a TabPanel with the matching id is rendered for the
  // selected tab. aria-controls used to be written unconditionally, which
  // pointed the Coach and Settings switchers at panels that do not exist and
  // axe reported as a critical aria-valid-attr-value (redo-1 fix round 2).
  readonly controlsPanels?: boolean;
}

export function readHashParam(hash: string, name: string): string | null {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  const value = new URLSearchParams(raw).get(name);
  return value && value.trim() !== '' ? value : null;
}

export function writeHashParam(hash: string, name: string, value: string): string {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  const params = new URLSearchParams(raw);
  params.set(name, value);
  return `#${params.toString()}`;
}

export function Tabs<T extends string>({ tabs, selected, onSelect, label, hashParam, className, end, controlsPanels = false }: TabsProps<T>) {
  const listRef = useRef<HTMLDivElement>(null);
  // Live view of the props for the event handlers, so the hash listeners are
  // registered once and never read a stale selection. Synced in an effect
  // (declared first, so later effects read the fresh values), never in render.
  const live = useRef({ tabs, selected, onSelect });
  useEffect(() => {
    live.current = { tabs, selected, onSelect };
  });
  // The exact hash string this component last wrote. A hash event that equals
  // it is our own write echoed back and must not be applied to state.
  const lastWritten = useRef<string | null>(null);

  // Mount with hashParam: adopt a valid hash value once (deep link support).
  useEffect(() => {
    if (!hashParam || typeof window === 'undefined') return;
    const fromHash = readHashParam(window.location.hash, hashParam);
    const { tabs: t, selected: s, onSelect: fire } = live.current;
    if (fromHash !== null && fromHash !== s && t.some((tab) => tab.id === fromHash)) fire(fromHash as T);
    // Mount-only adoption; later hash movement arrives through the events below.
  }, [hashParam]);

  // State to hash: replaceState so Back is not flooded and no event fires.
  useEffect(() => {
    if (!hashParam || typeof window === 'undefined') return;
    const next = writeHashParam(window.location.hash, hashParam, selected);
    lastWritten.current = next;
    if (next !== window.location.hash) {
      window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}${next}`);
    }
  }, [selected, hashParam]);

  // Real hash movement (user edits the hash, Back or Forward) to state.
  useEffect(() => {
    if (!hashParam || typeof window === 'undefined') return;
    const onHashEvent = (): void => {
      const current = window.location.hash;
      if (current === lastWritten.current) return;
      const fromHash = readHashParam(current, hashParam);
      const { tabs: t, selected: s, onSelect: fire } = live.current;
      if (fromHash !== null && fromHash !== s && t.some((tab) => tab.id === fromHash)) fire(fromHash as T);
    };
    window.addEventListener('hashchange', onHashEvent);
    window.addEventListener('popstate', onHashEvent);
    return () => {
      window.removeEventListener('hashchange', onHashEvent);
      window.removeEventListener('popstate', onHashEvent);
    };
  }, [hashParam]);

  const selectedIndex = Math.max(0, tabs.findIndex((t) => t.id === selected));

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    let target = -1;
    if (step !== 0) {
      let i = selectedIndex;
      for (let tries = 0; tries < tabs.length; tries += 1) {
        i = (i + step + tabs.length) % tabs.length;
        if (!tabs[i]?.disabled) { target = i; break; }
      }
    } else if (event.key === 'Home') target = tabs.findIndex((t) => !t.disabled);
    else if (event.key === 'End') target = tabs.length - 1 - [...tabs].reverse().findIndex((t) => !t.disabled);
    if (target < 0 || target === selectedIndex) return;
    event.preventDefault();
    const tab = tabs[target];
    if (!tab) return;
    onSelect(tab.id);
    listRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[target]?.focus();
  };

  return (
    <div className={cx('tabs-row', className)}>
      <div ref={listRef} role="tablist" aria-label={label} className="tabs" onKeyDown={onKeyDown}>
        {tabs.map((tab, index) => {
          const isSelected = index === selectedIndex;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              id={`tab-${tab.id}`}
              aria-selected={isSelected}
              {...(controlsPanels ? { 'aria-controls': `tabpanel-${tab.id}` } : {})}
              tabIndex={isSelected ? 0 : -1}
              disabled={tab.disabled}
              className="tab"
              onClick={() => onSelect(tab.id)}
            >
              {tab.label}
              {tab.count !== undefined ? <span className="tab-n num">{tab.count}</span> : null}
            </button>
          );
        })}
      </div>
      {end ? <div className="tabs-end">{end}</div> : null}
    </div>
  );
}

export interface TabPanelProps {
  readonly id: string;
  readonly active: boolean;
  readonly children: ReactNode;
  readonly className?: string;
}

export function TabPanel({ id, active, children, className }: TabPanelProps) {
  return (
    <div role="tabpanel" id={`tabpanel-${id}`} aria-labelledby={`tab-${id}`} hidden={!active} tabIndex={0} className={cx('tabpanel', className)}>
      {active ? children : null}
    </div>
  );
}
